import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { commercialWriteRecoveryInput } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { sameAccount, type AccountRef } from '../../lib/account';
import { onSignalOrVisible } from '../../lib/browser';
import {
  clearCommercialWrite,
  commercialRequestIssue,
  readCommercialWrite,
  reserveCommercialWrite,
  type CommercialAction,
  type CommercialRequests,
  type CommercialWriteRead,
} from '../../lib/commercial-write-recovery';
import { useSession } from '../session';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';

export function useCommercialWriteRecovery<A extends CommercialAction>(
  action: A,
  subject = 'new',
) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const reconcile = useMutation(
    trpc.commercialWrite.reconcileOrAbandon.mutationOptions(),
  );
  const session = useSession();
  const owner: AccountRef = { orgId: session.orgId, userId: session.userId };
  const key = JSON.stringify([owner.orgId, owner.userId, action, subject]);
  const current = useRef({ owner, key });
  const mounted = useRef(false);
  current.current = { owner, key };
  const [snapshot, setSnapshot] = useState<{
    key: string;
    read: CommercialWriteRead<A>;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const inFlight = useRef(false);
  const active = () =>
    mounted.current &&
    current.current.key === key &&
    sameAccount(current.current.owner, owner);
  const refresh = async () => {
    const read = await readCommercialWrite(owner, action, subject);
    if (active()) setSnapshot({ key, read });
    return read;
  };
  useEffect(() => {
    mounted.current = true;
    setError(null);
    setPreparing(false);
    void refresh();
    const stop = onSignalOrVisible(() => void refresh());
    return () => {
      mounted.current = false;
      stop();
    };
    // Identity/action changes invalidate the displayed saved request immediately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const read = snapshot?.key === key ? snapshot.read : { status: 'loading' as const };
  const prepare = async (request: CommercialRequests[A]) => {
    if (
      !active() ||
      inFlight.current ||
      read.status === 'loading' ||
      read.status === 'unreadable'
    )
      return false;
    const issue = commercialRequestIssue(action, request);
    if (issue) {
      setError(issue);
      return false;
    }
    inFlight.current = true;
    setPreparing(true);
    setError(null);
    try {
      const saved = await reserveCommercialWrite(owner, action, subject, request);
      await refresh();
      if (!active()) return false;
      if (!saved)
        setError(
          'This request could not be saved safely. Recover the saved request below, or restore browser storage before trying again. Nothing new was sent.',
        );
      return saved;
    } finally {
      inFlight.current = false;
      if (active()) setPreparing(false);
    }
  };
  const complete = async (request: { mutationId?: string }) => {
    const cleared = request.mutationId
      ? await clearCommercialWrite(owner, action, subject, request.mutationId)
      : false;
    await refresh();
    if (active() && !cleared)
      setError(
        'The record was saved, but its recovery receipt could not be cleared on this device. Retrying the saved request will return the same record.',
      );
    return active();
  };
  const resolveAttempt = async () => {
    if (
      !active() ||
      inFlight.current ||
      preparing ||
      reconcile.isPending ||
      read.status !== 'ready'
    )
      return false;
    inFlight.current = true;
    setError(null);
    try {
      const result = await reconcile.mutateAsync(
        commercialWriteRecoveryInput.parse({ action, request: read.request }),
      );
      if (!active()) return false;
      const cleared = await clearCommercialWrite(
        owner,
        action,
        subject,
        read.request.mutationId!,
      );
      await refresh();
      if (!active()) return false;
      if (!cleared) {
        setError(
          'The server resolved this request, but its saved receipt could not be cleared. Resolve it again before starting a replacement.',
        );
        return false;
      }
      void queryClient.invalidateQueries();
      if (result.status === 'committed') {
        const path = {
          commercial_job: 'jobs',
          quote: 'quotes',
          work_order: 'work-orders',
        }[result.destination.type];
        window.location.assign(`/${path}/${encodeURIComponent(result.destination.id)}`);
        return false;
      }
      return true;
    } catch (cause) {
      if (active())
        setError(
          cause instanceof Error
            ? cause.message
            : 'The request could not be resolved. Its original identity remains saved.',
        );
      return false;
    } finally {
      inFlight.current = false;
    }
  };
  return {
    read,
    error,
    preparing: preparing || reconcile.isPending,
    prepare,
    complete,
    resolveAttempt,
    refresh,
    isCurrent: active,
  };
}

export function CommercialWriteRecovery({
  read,
  error,
  pending,
  online,
  onRetry,
  onResolve,
}: {
  read: CommercialWriteRead<CommercialAction> | { status: 'loading' };
  error: string | null;
  pending: boolean;
  online: boolean;
  onRetry: () => void;
  onResolve: () => void;
}) {
  if (read.status === 'loading')
    return (
      <p className="text-xs text-muted-foreground">
        Checking this account's saved request…
      </p>
    );
  return (
    <>
      {error ? (
        <Notice tone="danger" alert>
          {error}
        </Notice>
      ) : null}
      {read.status === 'ready' || read.status === 'unreadable' ? (
        <div
          className="space-y-3 rounded-[var(--radius)] border border-border bg-surface-2 p-3"
          role="status"
        >
          <p className="text-sm font-semibold">Saved request needs recovery</p>
          <p className="text-sm">
            {read.status === 'ready'
              ? `Retry the exact saved request${'title' in read.request && read.request.title ? `: ${read.request.title}` : ''}. Its original payload and key are retained across reloads; a second record will not be created.`
              : 'This account has an unreadable saved request, or browser storage is unavailable. New submissions are blocked because its outcome cannot be safely assumed.'}
          </p>
          <p className="text-xs text-muted-foreground">
            You can close this panel to check existing records. This request stays saved
            for this account.
          </p>
          {read.status === 'ready' ? (
            <Button disabled={pending || !online} onClick={onRetry}>
              Retry saved request
            </Button>
          ) : null}
          {read.status === 'ready' ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Resolve attempt opens the saved result if it committed. Otherwise, the
                server blocks that original delivery before allowing you to correct and
                resubmit this form.
              </p>
              <Button
                variant="ghost"
                size="sm"
                disabled={pending || !online}
                onClick={onResolve}
              >
                Resolve attempt
              </Button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Restore site-storage access or reopen this same trusted browser profile
              before continuing. Do not clear its saved data: an unreadable request cannot
              be discarded safely.
            </p>
          )}
        </div>
      ) : null}
    </>
  );
}
